import { ApplicantType, VerificationNames } from "shared/types";
import { IncomeCriteria } from "./components/IncomeCriteria";
import { HousingCourtCriteria } from "./components/HousingCourtCriteria";
import { CriminalCriteria } from "./components/CriminalCriteria";
import { CreditReportAndScoreCriteria } from "./components/CreditReportAndScoreCriteria";
import { VerificationSchema } from "types/landlord/verificationTemplate";
import { Header } from "./components/Header";
import { Hide } from "shared/components/helpers/Hide";
import { useTemplateContext } from "shared/app/VerificationTemplate/provider";
import { ApplicantSettingsErrors } from "types/landlord/verificationTemplate";

export const ScoreCriteria = ({
  verificationSchemas,
  applicantSettingsErrors,
  onApplicantSettingsErrorsUpdate
}: {
  verificationSchemas: VerificationSchema[];
  applicantSettingsErrors?: ApplicantSettingsErrors | null;
  onApplicantSettingsErrorsUpdate?: (
    errors: ApplicantSettingsErrors | null
  ) => void;
}) => {
  const { readOnly, template, setTemplate } = useTemplateContext();
  const { recommendation_method, verification_schemas } = template || {};
  const criteriaEnabled = recommendation_method === "score_based";

  if (!template || !template.screening_criteria) return null;

  const updateCriteriaSettings = (
    criteriaSetting: string,
    key: string,
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (!template.screening_criteria) return;

    setTemplate({
      ...template,
      screening_criteria: {
        ...template.screening_criteria,
        applicant_settings: {
          ...template.screening_criteria.applicant_settings,
          [criteriaSetting]: {
            ...template.screening_criteria.applicant_settings?.[
              criteriaSetting
            ],
            [key]: e.target.value
          }
        }
      }
    });

    if (applicantSettingsErrors && onApplicantSettingsErrorsUpdate)
      onApplicantSettingsErrorsUpdate(
        ((
          prevErrors: ApplicantSettingsErrors | null
        ): ApplicantSettingsErrors => {
          if (!prevErrors) return {} as ApplicantSettingsErrors;

          const {
            [criteriaSetting as keyof ApplicantSettingsErrors]: _,
            ...restErrors
          } = prevErrors;
          return restErrors;
        })(applicantSettingsErrors)
      );
  };

  const isVerificationEnabled = (
    verificationName: VerificationNames,
    role: ApplicantType
  ) => {
    return Boolean(
      verification_schemas?.[role][verificationName] ||
        verificationSchemas[role]?.[verificationName]
    );
  };

  return (
    <div className="space-y-6">
      <Header />
      <Hide
        if={
          readOnly &&
          !isVerificationEnabled(
            VerificationNames.VerifiedIncomeVerification,
            ApplicantType.Applicant
          )
        }
      >
        <IncomeCriteria
          criteria={template.screening_criteria.applicant_settings}
          onValueChange={updateCriteriaSettings}
          applyCriteria={criteriaEnabled}
          readOnly={readOnly}
          applicantSettingsErrors={applicantSettingsErrors}
        />
      </Hide>

      <Hide
        if={
          readOnly &&
          !isVerificationEnabled(
            VerificationNames.CreditReport,
            ApplicantType.Applicant
          )
        }
      >
        <CreditReportAndScoreCriteria
          criteria={template.screening_criteria.applicant_settings}
          onValueChange={updateCriteriaSettings}
          applyCriteria={criteriaEnabled}
          readOnly={readOnly}
          applicantSettingsErrors={applicantSettingsErrors}
        />
      </Hide>

      <Hide
        if={
          readOnly &&
          !isVerificationEnabled(
            VerificationNames.EvictionReport,
            ApplicantType.Applicant
          )
        }
      >
        <HousingCourtCriteria
          criteria={template.screening_criteria.applicant_settings}
          onValueChange={updateCriteriaSettings}
          applyCriteria={criteriaEnabled}
          readOnly={readOnly}
        />
      </Hide>

      <Hide
        if={
          readOnly &&
          !isVerificationEnabled(
            VerificationNames.CriminalReport,
            ApplicantType.Applicant
          )
        }
      >
        <CriminalCriteria
          importance={
            template.screening_criteria.applicant_settings?.sex_offender_check
              ?.importance
          }
          disabled={Boolean(!criteriaEnabled || readOnly)}
          updateCriteriaSettings={updateCriteriaSettings}
        />
      </Hide>
    </div>
  );
};
